/**
 * Where the tilt reading comes from — see tilt.ts for what is done with it.
 *
 * Two sources, chosen by where the page is running:
 *
 * - The plugin, inside either app: `UIDevice` on iOS, `OrientationEventListener`
 *   on Android — the accelerometer read the way the system camera reads it,
 *   regardless of the interface lock, and already settled into a quadrant on
 *   the native side. Each shell has its reason for not taking the event below.
 *   WebKit puts `deviceorientation` behind `DeviceOrientationEvent.requestPermission`,
 *   which has to be called from a tap and answers with a system alert naming
 *   the origin — «localhost» — and that is not a dialog to put in front of a
 *   shutter. The Android WebView was meant to supply the event for free, and
 *   on the reporting phone (HONOR, MagicOS) it supplied nothing at all: no
 *   event, no turned icons, the photograph on its side. The Swift and Kotlin
 *   sides are both named DeviceTiltPlugin and answer the same shape.
 *
 * - `deviceorientation`, in a browser where it comes free — Chrome on Android.
 *   The event is in the phone's own frame whatever the page has done, so the
 *   page's own turn is subtracted (`relativeTilt`): with the rotation lock off
 *   the browser has already turned, and the photograph needs no turn at all.
 *
 * Safari on an iPhone gets neither: it is the gated case without a plugin, and
 * its photograph comes out the way it always has. A permission prompt for a
 * quarter-turn is the wrong trade there.
 */
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { gravityOf, relativeTilt, settleTilt, type Tilt } from './tilt.ts';

/** The plugin's name on all three sides — `jsName` in DeviceTiltPlugin.swift, `name` in DeviceTiltPlugin.kt. */
export const DEVICE_TILT_PLUGIN = 'DeviceTilt';

interface DeviceTiltPlugin {
  /** Start reading; the current reading follows at once. */
  start: () => Promise<void>;
  stop: () => Promise<void>;
  addListener: (
    event: 'tilt',
    handler: (info: { angle: number }) => void,
  ) => Promise<PluginListenerHandle>;
}

const DeviceTilt = registerPlugin<DeviceTiltPlugin>(DEVICE_TILT_PLUGIN);

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * True only inside either app, and only once the plugin is registered there — a
 * web bundle ahead of its shell takes the web path, as every plugin here does.
 * On Android that path is the one that answered nothing, so a shell without
 * the plugin is back to sideways photographs rather than to anything worse.
 */
export const hasNativeTilt: boolean = (() => {
  const capacitor = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
  const platform = capacitor?.getPlatform?.();
  if (platform !== 'ios' && platform !== 'android') return false;
  return capacitor?.isPluginAvailable?.(DEVICE_TILT_PLUGIN) === true;
})();

/** Whether the browser will only hand the reading over after a prompt. */
function gated(): boolean {
  const ctor = (globalThis as { DeviceOrientationEvent?: unknown }).DeviceOrientationEvent;
  return typeof ctor === 'function' && 'requestPermission' in ctor;
}

function screenAngle(): number {
  return typeof screen !== 'undefined' ? (screen.orientation?.angle ?? 0) : 0;
}

function asTilt(angle: number): Tilt {
  return ((((Math.round(angle / 90) * 90) % 360) + 360) % 360) as Tilt;
}

/**
 * Reports the tilt as it changes, starting from the current one where the
 * source can say. Returns the unsubscribe, synchronously — the same rule as
 * watchCallAudio, and for the same reason: an effect's cleanup can run before
 * an awaited subscription exists.
 */
export function watchTilt(onTilt: (tilt: Tilt) => void): () => void {
  if (hasNativeTilt) {
    let stopped = false;
    const listening = DeviceTilt.addListener('tilt', (info) => {
      if (!stopped) onTilt(relativeTilt(asTilt(info.angle), screenAngle()));
    });
    // Started only once the listener is in place, or the first reading — the
    // one for a phone already on its side when the camera opens — could land
    // before anyone is there to hear it.
    void listening
      .then(() => (stopped ? undefined : DeviceTilt.start()))
      .catch(() => {
        // Without the reading a photograph comes out as it did before this
        // existed. Not worth a message.
      });
    return () => {
      stopped = true;
      void listening.then((handle) => handle.remove()).catch(() => {});
      void DeviceTilt.stop().catch(() => {});
    };
  }

  if (typeof window === 'undefined' || gated()) return () => {};

  let tilt: Tilt = 0;
  let reported: Tilt | null = null;
  const onReading = (event: DeviceOrientationEvent) => {
    // A desktop fires the event with nothing in it.
    if (event.beta === null || event.gamma === null) return;
    tilt = settleTilt(tilt, gravityOf(event.beta, event.gamma));
    const relative = relativeTilt(tilt, screenAngle());
    if (relative === reported) return;
    reported = relative;
    onTilt(relative);
  };
  window.addEventListener('deviceorientation', onReading);
  return () => window.removeEventListener('deviceorientation', onReading);
}
